// bookmarkNavigator — a tab strip of bookmarks. The list comes from
// /api/bookmarks (which reads Dashboard.Report/definition/bookmarks/*).
// Clicking a tab sets the "bookmark" key in the Zustand filter store AND
// — P2-bookmarks — replaces the active filter set with the bookmark's
// saved filters (PBI bookmarks-are-views semantics). Re-clicking the
// active bookmark clears it.

import { useMemo } from "react";
import type { VisualProps } from "./types";
import { filterKey, useFilterStore, type FilterEntry, type FilterKey, type FilterOp } from "../state/filterStore";
import { useBookmarks, type BookmarkChild, type BookmarkFilter } from "../api/hooks";
import "./_static.css";

// P2-bookmarks: convert the flat bookmark filter shape into the Zustand
// store's keyed map. Field-presence rows (op="present") have no enforceable
// value and are skipped — keeping them would force a stray empty filter on
// every navigation. The bookmark name itself is also stored under the
// reserved "bookmark" key so other visuals can highlight the active view.
function bookmarkFiltersToStore(
  bookmarkName: string,
  filters: BookmarkFilter[] | undefined,
  source: string,
): Record<FilterKey, FilterEntry> {
  const out: Record<FilterKey, FilterEntry> = {};
  out["bookmark"] = { op: "eq", value: bookmarkName, source };
  if (!filters) return out;
  for (const f of filters) {
    if (!f.table || !f.column) continue;
    if (f.op === "present") continue;
    out[filterKey(f.table, f.column)] = {
      op: f.op as FilterOp,
      value: f.value as FilterEntry["value"],
      source,
    };
  }
  return out;
}

export function BookmarkNavigatorVisual({ spec }: VisualProps) {
  const { data, isLoading, error } = useBookmarks();
  const filters = useFilterStore((s) => s.filters);
  const current = filters["bookmark"];

  // PBI lets you target a navigator at a specific bookmark group. We don't
  // have that wiring yet — for now, show all top-level groups OR fall back to
  // any bookmark that has a displayName.
  const tabs = useMemo(() => {
    if (!data) return [] as Array<{ name: string; displayName: string; filters?: BookmarkFilter[] }>;
    // Prefer groups with children (PBI's "bookmark group" pattern).
    const groups = data.items.filter((g) => (g.children?.length ?? 0) > 0);
    if (groups.length > 0) {
      return groups.map((g) => ({ name: g.name, displayName: g.displayName, filters: g.filters }));
    }
    // Otherwise expose top-level named bookmarks.
    return data.items.map((g) => ({ name: g.name, displayName: g.displayName, filters: g.filters }));
  }, [data]);

  // P2-bookmarks: look up the full bookmark entry (including its filters)
  // by name. Prefer the flat byName map; fall back to children entries.
  const lookupBookmark = (name: string): BookmarkChild | undefined => {
    if (!data) return undefined;
    const direct = data.byName[name];
    if (direct) return direct;
    for (const g of data.items) {
      if (g.name === name) {
        return {
          name: g.name,
          displayName: g.displayName,
          activeSection: g.activeSection,
          filters: g.filters,
        };
      }
      for (const c of g.children ?? []) {
        if (c.name === name) return c;
      }
    }
    return undefined;
  };

  return (
    <div className="static-bookmarks">
      <div className="static-bookmarks-title">{spec.title ?? "Bookmarks"}</div>
      <div className="static-bookmarks-strip">
        {isLoading && <span className="static-bookmark-pill">Loading…</span>}
        {error && (
          <span className="static-bookmark-pill" title={String(error)}>
            (failed to load)
          </span>
        )}
        {!isLoading &&
          !error &&
          tabs.length === 0 && (
            <span className="static-bookmark-pill">No bookmarks</span>
          )}
        {tabs.map((t) => {
          const active = current?.value === t.name;
          return (
            <button
              key={t.name}
              type="button"
              className={
                active
                  ? "static-bookmark-pill is-active"
                  : "static-bookmark-pill"
              }
              onClick={() => {
                // P2-bookmarks: if clicking the active bookmark, clear the
                // filter set (PBI parity: bookmarks are toggles for the
                // same tab strip). Otherwise replace the entire filter
                // set with the bookmark's saved filters.
                if (active) {
                  useFilterStore.setState({ filters: {} });
                  return;
                }
                const bm = lookupBookmark(t.name);
                const bmFilters = bm?.filters ?? t.filters ?? [];
                useFilterStore.setState({
                  filters: bookmarkFiltersToStore(t.name, bmFilters, spec.id),
                });
              }}
              title={t.displayName}
            >
              {t.displayName}
            </button>
          );
        })}
      </div>
    </div>
  );
}
